// One row in the producer's ADR Script Book.
//
// Two visual modes: read-only display (the default) and inline-edit. The
// edit mode is opened from the parent so multiple rows can't be open at
// once. Keeps purely presentational concerns here; the parent owns the
// mutation hook and passes `onSave`.

import { type FrameRate, secondsToSmpteDisplay } from '@ohear/shared'
import { Badge, Button, Input, Select, Textarea } from '@ohear/ui'
import { useState } from 'react'
import type { CueRow, CueType } from '../../lib/api/producer-types'

const CATEGORY_VARIANT: Record<
  NonNullable<CueRow['predicted_category']>,
  'gray' | 'blue' | 'green' | 'amber' | 'red'
> = {
  whisper: 'blue',
  normal: 'gray',
  loud: 'amber',
  shout: 'amber',
  extreme: 'red',
}

const CUE_TYPE_OPTIONS: Array<{ value: CueType; label: string }> = [
  { value: 'dialogue', label: 'Dialogue' },
  { value: 'effort', label: 'Effort' },
  { value: 'narration', label: 'Narration' },
  { value: 'walla', label: 'Walla' },
  { value: 'reaction', label: 'Reaction' },
]

export interface CueRowDisplayProps {
  cue: CueRow
  frameRate: FrameRate
  selected: boolean
  onSelect: (next: boolean) => void
  /** Color the character badge — parent maps character_id -> color. */
  characterColor?: string
  isEditing: boolean
  onStartEdit: () => void
  onCancelEdit: () => void
  onSave: (patch: Partial<CueRow>) => void
  onSplit: () => void
  onMerge: () => void
}

export function CueRowDisplay({
  cue,
  frameRate,
  selected,
  onSelect,
  characterColor,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onSave,
  onSplit,
  onMerge,
}: CueRowDisplayProps) {
  const [draft, setDraft] = useState<Partial<CueRow>>({})

  const handleSave = () => {
    onSave(draft)
    setDraft({})
  }

  const inDisplay = secondsToSmpteDisplay(cue.in_sec, frameRate)
  const outDisplay = secondsToSmpteDisplay(cue.out_sec, frameRate)
  const category = cue.predicted_category

  return (
    <tr className="border-t border-[var(--border)] align-top">
      <td className="px-2 py-2 align-middle">
        <input
          type="checkbox"
          aria-label={`Select cue ${cue.cue_number}`}
          checked={selected}
          onChange={(e) => onSelect(e.target.checked)}
        />
      </td>
      <td className="px-2 py-2 text-[12px] font-mono text-[var(--text-2)]">{cue.cue_number}</td>
      <td className="px-2 py-2">
        <span
          className="inline-flex items-center rounded-[var(--radius-sm)] px-2 py-0.5 text-[11px] font-medium"
          style={{
            background: characterColor ?? 'var(--bg-3)',
            color: '#fff',
          }}
          title={cue.character_code ?? 'unassigned'}
        >
          {cue.character_code ?? '—'}
        </span>
      </td>
      <td className="px-2 py-2 text-[11px] font-mono tabular-nums text-[var(--text-2)]">
        {inDisplay}
        <br />
        {outDisplay}
      </td>
      <td className="px-2 py-2 text-[13px] text-[var(--text)]">
        {isEditing ? (
          <>
            <Select
              aria-label="Cue type"
              value={(draft.cue_type ?? cue.cue_type) as string}
              onChange={(e) => setDraft((d) => ({ ...d, cue_type: e.target.value as CueType }))}
              className="mb-2 h-8"
            >
              {CUE_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            {(draft.cue_type ?? cue.cue_type) === 'dialogue' ? (
              <Textarea
                aria-label="Line text"
                rows={2}
                defaultValue={cue.line_text ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, line_text: e.target.value }))}
              />
            ) : (
              <Input
                aria-label="Effort descriptor"
                defaultValue={cue.effort_descriptor ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, effort_descriptor: e.target.value }))}
              />
            )}
          </>
        ) : (
          <span>
            {cue.cue_type === 'dialogue'
              ? (cue.line_text ?? <em className="text-[var(--text-3)]">no text</em>)
              : (cue.effort_descriptor ?? <em className="text-[var(--text-3)]">no descriptor</em>)}
          </span>
        )}
      </td>
      <td className="px-2 py-2">
        {category ? (
          <Badge variant={CATEGORY_VARIANT[category]}>{category}</Badge>
        ) : (
          <span className="text-[11px] text-[var(--text-3)]">—</span>
        )}
      </td>
      <td className="px-2 py-2">
        {isEditing ? (
          <div className="flex gap-1">
            <Button size="sm" onClick={handleSave}>
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancelEdit}>
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={onStartEdit}>
              Edit
            </Button>
            <Button size="sm" variant="ghost" onClick={onSplit}>
              Split
            </Button>
            <Button size="sm" variant="ghost" onClick={onMerge}>
              Merge
            </Button>
          </div>
        )}
      </td>
    </tr>
  )
}
